import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Shield, Send, Loader2, Mail, ArrowLeft, KeyRound, Smartphone } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://kroytechnologygroup.com/wp-content/uploads/2022/01/KTG-Final-Logo-Flat-300x200.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, completeMfaLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [contactData, setContactData] = useState({ name: '', email: '', company: '', message: '' });

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaMethod, setMfaMethod] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [emailFallback, setEmailFallback] = useState(false);

  useEffect(() => {
    if (searchParams.get('contact') === 'true') {
      setShowContact(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.mfa_required) {
        setMfaToken(result.mfa_token);
        setMfaMethod(result.mfa_method);
        setMfaStep(true);
        setMfaCode('');
      } else {
        toast.success('Welcome back!');
        navigate(result.is_admin ? '/admin' : '/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid credentials';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (!mfaCode.trim()) { toast.error('Please enter a verification code'); return; }
    setMfaLoading(true);
    try {
      const user = await completeMfaLogin(mfaToken, mfaCode.trim());
      toast.success('Welcome back!');
      navigate(user.is_admin ? '/admin' : '/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid verification code';
      toast.error(message);
    } finally {
      setMfaLoading(false);
    }
  };

  const handleResendEmailCode = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/auth/mfa/resend-email-code`, { mfa_token: mfaToken, code: '' });
      toast.success('A new code has been sent to your email');
      setEmailFallback(true);
      setMfaCode('');
    } catch (error) {
      toast.error('Failed to send email code');
    } finally {
      setResending(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactData.name.trim() || !contactData.email.trim() || !contactData.message.trim()) {
      toast.error('Please fill in your name, email, and message');
      return;
    }
    setContactLoading(true);
    try {
      const res = await axios.post(`${API_URL}/contact`, contactData);
      toast.success(res.data.message);
      setContactSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Left Side - Branding */}
      <div className="auth-sidebar">
        <div className="text-center text-white">
          <img src={LOGO_URL} alt="AuditGuard" className="h-20 w-auto rounded-xl mx-auto mb-6 shadow-xl bg-white p-2" />
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            AuditGuard
          </h1>
          <p className="text-white/80 text-lg mb-8">
            Client Compliance Portal
          </p>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-left max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-8 w-8 text-[#FF6B35]" />
              <span className="font-semibold">Secure Access</span>
            </div>
            <p className="text-white/70 text-sm">
              Your compliance documents are protected with enterprise-grade security. All data is encrypted and stored securely.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="auth-content">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={LOGO_URL} alt="AuditGuard" className="h-16 w-auto rounded-xl mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              AuditGuard
            </h1>
          </div>

          {!showContact ? (
            mfaStep ? (
              /* MFA Verification Step */
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Two-Factor Authentication
                  </h2>
                  <p className="text-slate-600 mt-2">
                    {emailFallback
                      ? 'A verification code has been sent to your email.'
                      : mfaMethod === 'totp'
                        ? 'Enter the 6-digit code from your authenticator app.'
                        : 'A verification code has been sent to your email.'}
                  </p>
                </div>

                <div className="flex items-center justify-center mb-6">
                  <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                    {emailFallback || mfaMethod === 'email'
                      ? <Mail className="h-8 w-8 text-[#FF6B35]" />
                      : <Smartphone className="h-8 w-8 text-[#FF6B35]" />}
                  </div>
                </div>

                <form onSubmit={handleMfaVerify} className="space-y-6" data-testid="mfa-verify-form">
                  <div className="space-y-2">
                    <Label htmlFor="mfa-code">Verification Code</Label>
                    <Input
                      id="mfa-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={8}
                      placeholder={mfaMethod === 'totp' ? '000000' : 'Enter code from email'}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ''))}
                      required
                      autoFocus
                      className="h-14 text-center text-2xl tracking-[0.3em] font-mono"
                      data-testid="mfa-code-input"
                    />
                    <p className="text-xs text-slate-400 text-center">
                      {emailFallback
                        ? 'Code expires in 10 minutes. You can also use a backup code.'
                        : mfaMethod === 'totp'
                          ? 'You can also use a backup code'
                          : 'Code expires in 10 minutes. You can also use a backup code.'}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 btn-primary text-base"
                    disabled={mfaLoading}
                    data-testid="mfa-verify-btn"
                  >
                    {mfaLoading ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />Verifying...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <KeyRound className="h-5 w-5" />Verify &amp; Sign In
                      </div>
                    )}
                  </Button>
                </form>

                {(mfaMethod === 'email' || emailFallback) && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={handleResendEmailCode}
                      disabled={resending}
                      className="text-sm text-[#FF6B35] hover:underline disabled:opacity-50"
                      data-testid="resend-email-code-btn"
                    >
                      {resending ? 'Sending...' : "Didn't receive the code? Resend"}
                    </button>
                  </div>
                )}

                {mfaMethod === 'totp' && !emailFallback && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={handleResendEmailCode}
                      disabled={resending}
                      className="text-sm text-slate-500 hover:text-[#FF6B35] hover:underline disabled:opacity-50 inline-flex items-center gap-1.5"
                      data-testid="email-fallback-btn"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      {resending ? 'Sending...' : "Can't access your app? Send code to email instead"}
                    </button>
                  </div>
                )}

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => { setMfaStep(false); setMfaCode(''); setEmailFallback(false); }}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mx-auto"
                    data-testid="mfa-back-btn"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back to Sign In
                  </button>
                </div>
              </>
            ) : (
              <>
              {/* Login Form */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Sign in to your account
                </h2>
                <p className="text-slate-600 mt-2">
                  Access your compliance documents and continue where you left off.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-12"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="h-12 pr-12"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="toggle-password-btn"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 btn-primary text-base"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Signing in...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      Sign In
                      <ArrowRight className="h-5 w-5" />
                    </div>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-[#FF6B35]" data-testid="forgot-password-link">
                  Forgot your password?
                </Link>
              </div>

              {/* Contact Us CTA */}
              <div className="mt-8 pt-8 border-t border-slate-200 text-center">
                <p className="text-sm text-slate-500 mb-3">Don't have an account?</p>
                <Button
                  variant="outline"
                  className="w-full h-11 gap-2"
                  onClick={() => setShowContact(true)}
                  data-testid="show-contact-form-btn"
                >
                  <Mail className="h-4 w-4" />
                  Contact Us to Get Set Up
                </Button>
              </div>

              <div className="mt-6 text-center">
                <Link to="/" className="text-sm text-slate-500 hover:text-[#FF6B35] transition-colors">
                  &larr; Back to home
                </Link>
              </div>
            </>
            )
          ) : (
            <>
              {/* Contact Form */}
              {!contactSent ? (
                <>
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Contact Us
                    </h2>
                    <p className="text-slate-600 mt-2">
                      Fill out the form below and we'll get you set up on AuditGuard.
                    </p>
                  </div>

                  <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name">Your Name *</Label>
                      <Input
                        id="contact-name"
                        placeholder="John Smith"
                        value={contactData.name}
                        onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                        required
                        className="h-11"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-email">Email Address *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        placeholder="you@company.com"
                        value={contactData.email}
                        onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                        required
                        className="h-11"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-company">Company Name</Label>
                      <Input
                        id="contact-company"
                        placeholder="Acme Corp"
                        value={contactData.company}
                        onChange={(e) => setContactData({ ...contactData, company: e.target.value })}
                        className="h-11"
                        data-testid="contact-company-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-message">Message *</Label>
                      <textarea
                        id="contact-message"
                        placeholder="Tell us about your compliance needs..."
                        value={contactData.message}
                        onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                        required
                        rows={4}
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 btn-primary text-base gap-2"
                      disabled={contactLoading}
                      data-testid="contact-submit-btn"
                    >
                      {contactLoading ? (
                        <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</>
                      ) : (
                        <><Send className="h-5 w-5" /> Send Inquiry</>
                      )}
                    </Button>
                  </form>
                </>
              ) : (
                /* Success State */
                <div className="text-center py-8" data-testid="contact-success">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Mail className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Inquiry Sent!
                  </h2>
                  <p className="text-slate-600 mb-2">
                    Thank you for reaching out. We'll get back to you shortly.
                  </p>
                  <p className="text-sm text-slate-400">
                    A copy of your inquiry has been sent to <strong>{contactData.email}</strong>.
                  </p>
                </div>
              )}

              {/* Back to login */}
              <div className="mt-8 pt-8 border-t border-slate-200 text-center">
                <p className="text-sm text-slate-500 mb-3">Already have an account?</p>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => { setShowContact(false); setContactSent(false); }}
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Sign In
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
